import { useModal } from '@billy/design-system'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'

import { useUpdateQuoteStatus } from '@features/invoices/hooks/quotes/useUpdateQuoteStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useSegment } from '../../../../hooks'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { pendingQuoteStatuses } from '../../constants/pendingQuoteStatuses'
import { DepositInvoiceDetailsForm } from '../../elements/DepositInvoiceDetailsModal/types/depositInvoiceDetailsForm'
import { QuoteStatus } from '../../enums/quoteStatus'
import type { RelatedInvoice } from '../../types/relatedInvoice'
import { getRelatedInvoiceRoute } from '../../utils/depositInvoices/getRelatedInvoiceRoute'
import type { DiscountModeTrackingValue } from '../../utils/getDiscountModeTrackingValue'

interface UseConvertQuoteToDepositInvoiceProps {
  confirmationModalId: string
  depositInvoiceDetailsModalId: string
  existingDepositInvoice?: RelatedInvoice
  onUpgradeClick?: () => void
  quoteId?: number
  quoteStatus?: QuoteStatus
  trackingDiscountMode?: DiscountModeTrackingValue
  trackingEventName?: string
}

export const useConvertQuoteToDepositInvoice = ({
  confirmationModalId,
  depositInvoiceDetailsModalId,
  existingDepositInvoice,
  onUpgradeClick,
  quoteId,
  quoteStatus,
  trackingDiscountMode,
  trackingEventName,
}: UseConvertQuoteToDepositInvoiceProps) => {
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { track } = useSegment()
  const { updateQuoteStatus } = useUpdateQuoteStatus()

  const { open: openConfirmationModal, close: closeConfirmationModal } = useModal(confirmationModalId)
  const { open: openDepositInvoiceModal, close: closeDepositInvoiceModal } = useModal(depositInvoiceDetailsModalId)

  const isPending = !!quoteStatus && pendingQuoteStatuses.includes(quoteStatus)

  const onConvertClick = useCallback(() => {
    if (existingDepositInvoice) {
      const pathname = getOrganizationViewUrl(organization?.url, getRelatedInvoiceRoute(existingDepositInvoice))
      navigate(pathname)
      return
    }
    if (onUpgradeClick) {
      onUpgradeClick()
      return
    }
    if (trackingEventName) {
      track(trackingEventName, { discountMode: trackingDiscountMode, option: 'depositInvoice' })
    }
    if (isPending) {
      openConfirmationModal()
    } else {
      openDepositInvoiceModal()
    }
  }, [
    existingDepositInvoice,
    isPending,
    navigate,
    onUpgradeClick,
    openConfirmationModal,
    openDepositInvoiceModal,
    organization?.url,
    track,
    trackingDiscountMode,
    trackingEventName,
  ])

  const onConfirmConvert = useCallback(() => {
    if (!quoteId) {
      return
    }
    updateQuoteStatus(
      { quoteId: String(quoteId), value: QuoteStatus.Accepted },
      {
        onSuccess: () => {
          closeConfirmationModal()
          openDepositInvoiceModal()
        },
        onError: () => {
          closeConfirmationModal()
        },
      },
    )
  }, [closeConfirmationModal, openDepositInvoiceModal, quoteId, updateQuoteStatus])

  const onDepositInvoiceDetailsConfirm = useCallback(
    (formData: DepositInvoiceDetailsForm) => {
      const searchParams = new URLSearchParams()
      searchParams.append('depositAmount', formData.amount.toFixed(2))
      searchParams.append('depositType', formData.type)

      const depositsUrl = reactRoute.organization.invoices.getDepositInvoiceFromQuoteRoute(quoteId?.toString())

      const pathname = `${getOrganizationViewUrl(organization?.url, depositsUrl)}`

      navigate({ pathname, search: searchParams.toString() })
      closeDepositInvoiceModal()
    },
    [closeDepositInvoiceModal, navigate, organization, quoteId],
  )

  return {
    onConfirmConvert,
    onConvertClick,
    onDepositInvoiceDetailsClose: closeDepositInvoiceModal,
    onDepositInvoiceDetailsConfirm,
  }
}
